import React, { useCallback, useEffect, useRef } from 'react';

import { EChartsInstance, EChartsReactProps } from 'echarts-for-react';
import EChartsReactCore from 'echarts-for-react/lib/core';
import {
    BarChart,
    CustomChart,
    GraphChart,
    HeatmapChart,
    LineChart,
    MapChart,
    PieChart,
    ScatterChart,
    TreemapChart,
} from 'echarts/charts';
import { MapChart as EchartsMapChart } from 'echarts/charts';
import {
    DataZoomComponent,
    DatasetComponent,
    GeoComponent,
    GraphicComponent,
    GridComponent,
    LegendComponent,
    MarkLineComponent,
    TimelineComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
    VisualMapComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import SaveAsImage from 'echarts/lib/component/toolbox/feature/SaveAsImage';
import { CanvasRenderer } from 'echarts/renderers';
import Resize from 'element-resize-detector';
import { debounce } from 'lodash/fp';

echarts.use([
    BarChart,
    CustomChart,
    CanvasRenderer,
    DatasetComponent,
    DataZoomComponent,
    EchartsMapChart,
    GeoComponent,
    GraphChart,
    GraphicComponent,
    GridComponent,
    HeatmapChart,
    LegendComponent,
    LineChart,
    MapChart,
    MarkLineComponent,
    PieChart,
    TimelineComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
    TreemapChart,
    SaveAsImage,
    ScatterChart,
    VisualMapComponent,
]);

type Props = EChartsReactProps & {
    onChartResize?: (width: number, height: number) => void;
};

const EChartsWrapper: React.FC<Props> = ({ onChartResize, style, ...props }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<EChartsInstance | null>(null);
    const resizer = useRef<ReturnType<typeof Resize> | null>(null);

    const resizeHandler = useCallback(
        (width: number, height: number) => {
            onChartResize?.(width, height);
            chartInstance.current?.resize();
        },
        [onChartResize]
    );

    const onWindowResize = useCallback(
        debounce(250, () => {
            if (!chartInstance.current) return;
            resizeHandler(chartInstance.current.getWidth(), chartInstance.current.getHeight());
        }),
        [resizeHandler]
    );

    useEffect(() => {
        window.addEventListener('resize', onWindowResize);
        resizer.current = Resize({ strategy: 'scroll' });

        if (chartRef.current) {
            resizer.current.listenTo(chartRef.current, (el) => {
                resizeHandler(el.offsetWidth, el.offsetHeight);
            });
        }

        return () => {
            window.removeEventListener('resize', onWindowResize);
            resizer.current?.uninstall();
        };
    }, [onWindowResize, resizeHandler]);

    const onChartReady = useCallback(
        (instance: EChartsInstance) => {
            chartInstance.current = instance;
            props.onChartReady?.(instance);
        },
        [props.onChartReady]
    );

    return (
        <div
            ref={chartRef}
            style={{
                width: style?.width,
                height: isFinite(style?.height) ? style.height : 0,
            }}
        >
            <EChartsReactCore
                echarts={echarts}
                notMerge
                {...props}
                {...{ style }}
                onChartReady={onChartReady}
            />
            <canvas id="canvasFontSizer" width="0" height="0" />
        </div>
    );
};

export default EChartsWrapper;
